import type {
  TeammatesFilterStatus,
  TeammatesFilterType,
} from './useTeammatesFilter';
import type {
  TeammatesInvitationFragment,
  TeammatesUserFragment,
} from '../../graphql/generated/graphql';
import {
  InvitationStatus,
  ProvisionType,
  UserType,
} from '../../graphql/generated/graphql';

export const getSearchFilter = (query = '') => {
  const lowercasedQuery = query.toLowerCase();

  return (
    teammate?: TeammatesUserFragment | TeammatesInvitationFragment | null,
  ) =>
    lowercasedQuery
      ? teammate?.name?.toLowerCase().includes(lowercasedQuery) ||
        teammate?.email?.toLowerCase().includes(lowercasedQuery)
      : true;
};

const getUsersTypeFilter = (types: TeammatesFilterType[]) => {
  if (types.length === 0) {
    return () => true;
  }
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const getAdmins = types.includes('ADMIN');
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const getGuests = types.includes('GUEST');
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const getUsers = types.includes('USER');

  return (user?: TeammatesUserFragment | null) =>
    user &&
    ((getAdmins && user.type === UserType.Admin) ||
      (getGuests && user.type === UserType.Guest) ||
      (getUsers && user.type === UserType.User));
};

const getUsersStatusFilter = (statuses: TeammatesFilterStatus[]) => {
  if (statuses.length === 0) {
    return () => true;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const getActive = statuses.includes('ACTIVE');
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const getDeactivated = statuses.includes('DEACTIVATED');

  return (user?: TeammatesUserFragment | null) =>
    user &&
    ((getActive && !user.deactivatedAt) ||
      (getDeactivated && user.deactivatedAt));
};

export const getUsersFilter = (
  types: TeammatesFilterType[],
  statuses: TeammatesFilterStatus[],
) => {
  const usersTypeFilter = getUsersTypeFilter(types);
  const usersStatusFilter = getUsersStatusFilter(statuses);

  return (user?: TeammatesUserFragment | null) =>
    usersTypeFilter(user) && usersStatusFilter(user);
};

const getInvitationsTypeFilter = (types: TeammatesFilterType[]) => {
  if (types.length === 0) {
    return () => true;
  }
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const getAdmins = types.includes('ADMIN');
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const getGuests = types.includes('GUEST');
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const getUsers = types.includes('USER');

  return (invitation?: TeammatesInvitationFragment | null) =>
    invitation &&
    ((getAdmins && invitation.type === ProvisionType.Admin) ||
      (getGuests && invitation.type === ProvisionType.Guest) ||
      (getUsers && invitation.type === ProvisionType.User));
};

const getInvitationsStatusFilter = (statuses: TeammatesFilterStatus[]) => {
  if (statuses.length === 0) {
    return () => true;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const getInvited = statuses.includes('INVITED');
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const getPending = statuses.includes('PENDING');
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const getRejected = statuses.includes('REJECTED');

  return (invitation?: TeammatesInvitationFragment | null) =>
    invitation &&
    ((getInvited && invitation.status === InvitationStatus.Sent) ||
      (getPending && invitation.status === InvitationStatus.Pending) ||
      (getRejected && invitation.status === InvitationStatus.Rejected));
};

export const getInvitationsFilter = (
  types: TeammatesFilterType[],
  statuses: TeammatesFilterStatus[],
) => {
  const invitationsTypeFilter = getInvitationsTypeFilter(types);
  const invitationsStatusFilter = getInvitationsStatusFilter(statuses);

  return (invitation?: TeammatesInvitationFragment | null) =>
    invitationsTypeFilter(invitation) && invitationsStatusFilter(invitation);
};
